import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { fetchCookieConsent, updateProfile } from "@/services/db/profiles";

/**
 * Syncs cookie consent between localStorage and Supabase profiles.
 *
 * On login: localStorage wins if it has consent; otherwise restore from DB.
 * On consent change while logged in: push to DB.
 */
export function useCookieConsentSync() {
  const { user, loading } = useAuth();
  const { preferences, savePreferences } = useCookieConsent();

  // Prevents the "push to DB" effect from firing right after a DB restore
  const skipNextSyncRef = useRef(false);
  // Track the previous user ID to detect login transitions
  const prevUserIdRef = useRef<string | null>(null);

  // Effect 1: On login — sync localStorage ↔ DB
  useEffect(() => {
    if (loading) return;

    const userId = user?.id ?? null;
    const prevUserId = prevUserIdRef.current;
    prevUserIdRef.current = userId;

    // Only run on login transition (null → userId)
    if (!userId || userId === prevUserId) return;

    // Local consent exists → push to DB
    if (preferences.hasConsented) {
      updateProfile(userId, {
        cookie_consent_analytics: preferences.analytics,
        cookie_consent_timestamp: preferences.consentTimestamp,
      }).catch(() => {
        // Best-effort — localStorage remains source of truth
      });
      return;
    }

    // No local consent → try restoring from DB
    fetchCookieConsent(userId)
      .then((dbConsent) => {
        if (dbConsent?.analytics != null) {
          skipNextSyncRef.current = true;
          savePreferences(dbConsent.analytics);
        }
      })
      .catch(() => {
        // Best-effort — user will see the banner
      });
  }, [user?.id, loading, preferences.hasConsented, preferences.analytics, preferences.consentTimestamp, savePreferences]);

  // Effect 2: On consent change while logged in → push to DB
  useEffect(() => {
    if (!user?.id || !preferences.hasConsented) return;

    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    updateProfile(user.id, {
      cookie_consent_analytics: preferences.analytics,
      cookie_consent_timestamp: preferences.consentTimestamp,
    }).catch(() => {
      // Best-effort — localStorage remains source of truth
    });
  }, [user?.id, preferences.hasConsented, preferences.analytics, preferences.consentTimestamp]);
}
